import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SendIcon from "@mui/icons-material/Send";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import type { Dayjs, InstantRange, TimeRangeOnDate } from "@thalamos/common";
import { dayjs, UK_TIME_ZONE } from "@thalamos/common";
import type {
  ExtendedThalamosUser,
  InterviewContactMethod,
  InterviewParticipantStatus,
  InterviewWithParticipants,
  RouteTimeResult,
  Section12DoctorSearchResult,
  Section12Gender,
  Section12RequestUserStatus,
} from "@thalamos/aspire-common";
import {
  ASSESSMENT_DURATION_MINUTES,
  isAmhpAccreditation,
  ROUTE_TIME_MAX_USER_IDS,
} from "@thalamos/aspire-common";

import {
  defaultRequestedRanges,
  formatTimeRangeOnDate,
  requestFromInstants,
  unusableRanges,
} from "../RequestedAvailability/requestedAvailability.js";
import { AvailabilityFilterPopout } from "./components/AvailabilityFilterPopout.js";
import { ContactDoctorsConfirmDialog } from "./components/ContactDoctorsConfirmDialog.js";
import { DoctorList } from "./components/DoctorList.js";
import { EmptyState } from "./components/EmptyState.js";
import { FilterPanel } from "./components/FilterPanel.js";
import type { SelectedLocalAuthority } from "./components/LocalAuthorityPicker.js";
import { LocalAuthorityPicker } from "./components/LocalAuthorityPicker.js";
import { parseDoctorSearchResults } from "./utils/doctorSearchResults.js";
import { api, apiHooks } from "~/api.js";
import {
  Banner,
  BannerList,
} from "~/components/design-system/Banner/Banner.js";
import {
  AddressField,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { config } from "~/config.js";
import { ManagedLocationsContext } from "~/Contexts.js";
import { logActionToDatadog, logErrorToDatadog } from "~/tracing.js";

/** Map interview participant statuses to S12 request user statuses for DoctorCard display. */
function mapParticipantStatus(
  status: InterviewParticipantStatus,
): Section12RequestUserStatus {
  switch (status) {
    case "pending":
      return "pending";
    case "accepted":
      return "accepted";
    case "rejected":
      return "rejected";
    case "confirmed":
      return "accepted";
    case "revoked":
      return "withdrawn";
    case "pending-confirmed":
      // Doctor has accepted and been confirmed — treat as accepted (locked) for display.
      return "accepted";
  }
}

/** Route times, with the search they answer, so a set from an earlier search can be recognised. */
interface FetchedRouteTimes {
  doctors: Section12DoctorSearchResult[];
  postalCode: string;
  byDoctorId: Map<string, RouteTimeResult>;
}

interface S12DoctorRequestDialogProps {
  interview: InterviewWithParticipants;
  user: ExtendedThalamosUser;
  onClose: () => void;
  reloadFormContext: () => void;
}

/**
 * The time to search against: what the interview already asks for, or the next
 * few hours when it asks for nothing. A time agreed to the second has no
 * faithful range, so those fall back to the default too.
 */
function seedRequestedRanges(
  timeRanges: InstantRange[],
  now: Dayjs,
): TimeRangeOnDate[] {
  if (timeRanges.length === 0) {
    return defaultRequestedRanges(now);
  }
  return requestFromInstants(timeRanges)
    .map((request) => request.ranges)
    .unwrapOr(defaultRequestedRanges(now));
}

export function S12DoctorRequestDialog({
  interview,
  user,
  onClose,
  reloadFormContext,
}: S12DoctorRequestDialogProps) {
  // Assessment location state — initialised from the interview
  const [assessmentAddress, setAssessmentAddress] = useState(
    interview.address ?? "",
  );
  const [assessmentPostalCode, setAssessmentPostalCode] = useState(
    interview.postalCode ?? "",
  );
  const [assessmentLocationUnknown, setAssessmentLocationUnknown] = useState(
    interview.locationUnknown,
  );
  const [isSavingLocation, setIsSavingLocation] = useState(false);

  // Location entry pre-state: show when no location has been set yet
  const hasExistingLocation =
    interview.locationUnknown || !!interview.postalCode?.trim();
  const [showLocationEntry, setShowLocationEntry] =
    useState(!hasExistingLocation);

  const [fetchedRouteTimes, setFetchedRouteTimes] =
    useState<FetchedRouteTimes | null>(null);
  // State management
  const [selectedLocalAuthority, setSelectedLocalAuthority] =
    useState<SelectedLocalAuthority>(null);
  const [selectedSpecialismIds, setSelectedSpecialismIds] = useState<string[]>(
    [],
  );
  const [selectedGenders, setSelectedGenders] = useState<Section12Gender[]>([]);
  const [selectedLanguageIds, setSelectedLanguageIds] = useState<string[]>([]);
  // UK wall clock, as every `now` given to `defaultRequestedRanges` must be.
  const now = useMemo(() => dayjs().tz(UK_TIME_ZONE), []);
  const [requestedRanges, setRequestedRanges] = useState<TimeRangeOnDate[]>(
    () => seedRequestedRanges(interview.timeRanges, now),
  );
  const [selectedDoctorIds, setSelectedDoctorIds] = useState<Set<string>>(
    new Set(),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showMobileLAPicker, setShowMobileLAPicker] = useState(false);

  // Per-card contact state
  const [contactingDoctorIds, setContactingDoctorIds] = useState<Set<string>>(
    new Set(),
  );
  const [localContactedMap, setLocalContactedMap] = useState<
    Map<string, Section12RequestUserStatus>
  >(new Map());

  const managedLocationsContext = useContext(ManagedLocationsContext);

  // Default LA from AMHP accreditation
  useEffect(() => {
    const amhpAccreditation = user.accreditations?.find(isAmhpAccreditation);
    if (amhpAccreditation?.localAuthority) {
      const laName = amhpAccreditation.localAuthority;
      // Names are unique only within a type, so matching on name alone can
      // select a board rather than the accrediting authority.
      const localAuthority = managedLocationsContext.managedLocations.find(
        (loc) =>
          loc.type === "localAuthority" &&
          loc.name.toLowerCase() === laName.toLowerCase(),
      );

      if (localAuthority) {
        setSelectedLocalAuthority({
          id: localAuthority.id,
          name: localAuthority.name,
        });
      } else {
        setSelectedLocalAuthority({ name: laName });
      }
    }
  }, [user, managedLocationsContext]);

  // API hooks
  const [specialismsResult] = apiHooks.section12.getAllSpecialisms();
  const [languagesResult] = apiHooks.section12.getAllLanguages();
  const [doctorsResult] = apiHooks.section12.searchDoctors(
    selectedLocalAuthority?.id || null,
    interview.id,
  );

  const specialisms = specialismsResult.data || [];
  const languages = languagesResult.data || [];
  const parsedDoctors = useMemo(
    () => parseDoctorSearchResults(doctorsResult.data ?? []),
    [doctorsResult.data],
  );
  const doctors = parsedDoctors.doctors;
  // Logging is a side effect; `parseDoctorSearchResults` stays pure so it can
  // run in the memo above.
  useEffect(() => {
    for (const { doctorId, error } of parsedDoctors.unreadable) {
      logErrorToDatadog(error, { doctorId });
    }
  }, [parsedDoctors.unreadable]);
  const isLoadingDoctors = doctorsResult.loading;
  const doctorsError = doctorsResult.error;

  // Gated on !showLocationEntry because the server reads the postcode from the
  // saved interview, so route times are only meaningful once it is confirmed.
  const routeTimesNeeded =
    !showLocationEntry &&
    !assessmentLocationUnknown &&
    assessmentPostalCode !== "" &&
    doctors.length > 0;
  const currentRouteTimes =
    routeTimesNeeded &&
    fetchedRouteTimes?.doctors === doctors &&
    fetchedRouteTimes.postalCode === assessmentPostalCode
      ? fetchedRouteTimes.byDoctorId
      : undefined;
  const routeTimesLoading = routeTimesNeeded && currentRouteTimes === undefined;
  const routeTimes = useMemo(
    () => currentRouteTimes ?? new Map<string, RouteTimeResult>(),
    [currentRouteTimes],
  );

  // Emit a Datadog log once per completed search so the frontend event can
  // be joined with the backend `S12 doctor search` log and we capture the
  // interview context (which the backend endpoint doesn't have access to).
  // Gated on a loading true→false transition: axios-hooks keeps stale `data`
  // visible while refetching, so logging eagerly on LA change would record
  // the previous LA's `resultCount` against the new LA id.
  const lastLoggedLaIdRef = useRef<string | null>(null);
  const prevLoadingRef = useRef<boolean>(false);
  useEffect(() => {
    const laId = selectedLocalAuthority?.id;
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = doctorsResult.loading;

    if (
      laId &&
      wasLoading &&
      !doctorsResult.loading &&
      doctorsResult.data &&
      lastLoggedLaIdRef.current !== laId
    ) {
      lastLoggedLaIdRef.current = laId;
      logActionToDatadog("S12 doctor search", {
        interviewId: interview.id,
        formContextId: interview.formContextId,
        localAuthorityId: laId,
        localAuthorityName: selectedLocalAuthority.name,
        resultCount: doctorsResult.data.length,
        address: interview.address,
        postalCode: interview.postalCode,
        timeRanges: interview.timeRanges,
      });
    }
  }, [
    doctorsResult.loading,
    doctorsResult.data,
    selectedLocalAuthority?.id,
    selectedLocalAuthority?.name,
    interview.id,
    interview.formContextId,
    interview.address,
    interview.postalCode,
    interview.timeRanges,
  ]);

  // Map existing interview participants by userId for quick status lookup,
  // merged with optimistic local contact state
  const existingStatusByUserId = useMemo(() => {
    const map = new Map(
      interview.participants.map((p) => [
        p.userId,
        mapParticipantStatus(p.status),
      ]),
    );
    // Overlay optimistic local contacts only for doctors not yet present in server state
    for (const [userId, status] of localContactedMap) {
      if (!map.has(userId)) {
        map.set(userId, status);
      }
    }
    return map;
  }, [interview.participants, localContactedMap]);

  const criteria = useMemo(
    () => ({
      specialismIds: selectedSpecialismIds,
      genderValues: selectedGenders,
      languageIds: selectedLanguageIds,
      requestedRanges,
    }),
    [
      selectedSpecialismIds,
      selectedGenders,
      selectedLanguageIds,
      requestedRanges,
    ],
  );

  const unusable = useMemo(
    () => unusableRanges(requestedRanges),
    [requestedRanges],
  );
  // A range nobody could keep is not a search anyone can answer.
  const searchBlocked = unusable.length > 0;

  // A changed search is a new search: contacting doctors is gated only on how
  // many are selected, so nobody picked against earlier criteria, or another
  // authority's list, should carry into it. Keyed on the authority as well as
  // `doctors`, which keeps the previous authority's results while a search
  // loads, fails or never runs.
  useEffect(() => {
    setSelectedDoctorIds(new Set());
  }, [selectedLocalAuthority?.id, doctors, criteria]);

  // Handle per-card contact (EMAIL, SMS, or manual)
  const handleContactDoctor = useCallback(
    async (doctorId: string, method: InterviewContactMethod[]) => {
      setContactingDoctorIds((prev) => new Set(prev).add(doctorId));
      setSubmitError(null);

      try {
        const response = await api.interviews.addParticipant(interview.id, {
          userId: doctorId,
          contactMethod: method,
          inviteSource: "search",
        });

        if (response.status >= 200 && response.status < 300) {
          // Optimistically mark as contacted
          setLocalContactedMap((prev) => {
            const next = new Map(prev);
            next.set(doctorId, "pending");
            return next;
          });
          // Remove from selection
          setSelectedDoctorIds((prev) => {
            const next = new Set(prev);
            next.delete(doctorId);
            return next;
          });
          const label =
            method.length === 1 && method[0] === "email"
              ? "Email sent"
              : method.length === 1 && method[0] === "sms"
                ? "SMS sent"
                : method.length === 1 && method[0] === "manual"
                  ? "Marked as contacted"
                  : method.length === 1 && method[0] === "call"
                    ? "Marked as called"
                    : "Invitation sent";
          renderSuccessToast({ message: label });
        } else {
          const data = response.data as
            { message?: string; reason?: string } | undefined;
          setSubmitError(
            data?.message ?? data?.reason ?? "Failed to contact doctor",
          );
        }
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to contact doctor. Please try again.";
        setSubmitError(message);
      } finally {
        setContactingDoctorIds((prev) => {
          const next = new Set(prev);
          next.delete(doctorId);
          return next;
        });
      }
    },
    [interview.id],
  );

  // Handle "Mark as Contacted" for all selected doctors
  const handleMarkAsContacted = useCallback(async () => {
    const doctorIds = Array.from(selectedDoctorIds).filter((id) => {
      const status = existingStatusByUserId.get(id);
      return status !== "pending" && status !== "accepted";
    });
    if (doctorIds.length === 0) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Each outcome carries the doctor it belongs to, so a partial failure
      // cannot mis-attribute a response to the wrong invitation.
      const outcomes = await Promise.all(
        doctorIds.map(async (userId) => ({
          userId,
          settled: await api.interviews
            .addParticipant(interview.id, {
              userId,
              contactMethod: ["manual"],
              inviteSource: "search",
            })
            .then((response) => ({ ok: true as const, response }))
            .catch((reason: unknown) => ({ ok: false as const, reason })),
        })),
      );

      const succeededIds: string[] = [];
      const failures: string[] = [];

      for (const { userId, settled } of outcomes) {
        if (!settled.ok) {
          const err = settled.reason as { message?: string } | undefined;
          failures.push(err?.message ?? "Network error");
          continue;
        }

        const status = settled.response.status;
        if (status >= 200 && status < 300) {
          succeededIds.push(userId);
        } else {
          const data = settled.response.data as
            { message?: string; reason?: string } | undefined;
          failures.push(
            data?.message ??
              data?.reason ??
              `Request failed (${String(status)})`,
          );
        }
      }

      if (succeededIds.length > 0) {
        // Optimistically mark as contacted
        setLocalContactedMap((prev) => {
          const next = new Map(prev);
          for (const id of succeededIds) {
            next.set(id, "pending");
          }
          return next;
        });
        setSelectedDoctorIds(new Set());
        renderSuccessToast({
          message: `Marked ${String(succeededIds.length)} doctor${succeededIds.length !== 1 ? "s" : ""} as contacted`,
        });
      }

      if (failures.length > 0) {
        setSubmitError(`${String(failures.length)} failed: ${failures[0]}`);
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to mark as contacted.";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedDoctorIds, existingStatusByUserId, interview.id]);

  useEffect(() => {
    if (!routeTimesNeeded) {
      return;
    }

    let cancelled = false;
    const postalCode = assessmentPostalCode;
    const allUserIds = doctors.map((d) => d.id);

    // Batch userIds to stay within the API limit
    const batches: string[][] = [];
    for (let i = 0; i < allUserIds.length; i += ROUTE_TIME_MAX_USER_IDS) {
      batches.push(allUserIds.slice(i, i + ROUTE_TIME_MAX_USER_IDS));
    }

    void Promise.all(
      batches.map((userIds) =>
        api.interviews.getRouteTimes(interview.id, { userIds }),
      ),
    )
      .then((results) => {
        const map = new Map<string, RouteTimeResult>();
        results
          .flatMap((r) => r.data)
          .forEach((rt) => map.set(rt.doctorId, rt));
        return map;
      })
      // A failed lookup settles as no route times, so cards stop showing them as
      // loading, at the cost of not applying anyone's maximum travel time.
      .catch(() => new Map<string, RouteTimeResult>())
      .then((byDoctorId) => {
        if (!cancelled) {
          setFetchedRouteTimes({ doctors, postalCode, byDoctorId });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [routeTimesNeeded, doctors, assessmentPostalCode, interview.id]);

  // Save assessment location to the interview
  const handleSaveAssessmentLocation = async () => {
    setIsSavingLocation(true);
    try {
      await api.interviews.update(interview.id, {
        address: assessmentLocationUnknown ? null : assessmentAddress || null,
        postalCode: assessmentLocationUnknown
          ? null
          : assessmentPostalCode || null,
        locationUnknown: assessmentLocationUnknown,
      });
    } catch {
      // TODO(s12-location): a failed save leaves route times computed against
      // the previously saved postcode.
    } finally {
      setIsSavingLocation(false);
    }
  };

  // Confirm location and proceed to doctor list
  const handleConfirmLocation = async () => {
    await handleSaveAssessmentLocation();
    setShowLocationEntry(false);
  };

  const handleToggleDoctor = (doctorId: string, selected: boolean) => {
    setSelectedDoctorIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(doctorId);
      } else {
        next.delete(doctorId);
      }
      return next;
    });
  };

  const handleClearFilters = () => {
    setSelectedSpecialismIds([]);
    setSelectedGenders([]);
    setSelectedLanguageIds([]);
  };

  const handleSubmit = () => {
    if (selectedDoctorIds.size === 0) {
      setSubmitError("Please select at least one doctor");
      return;
    }

    // Open confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Add each selected doctor as a participant on the interview.
      // We use allSettled because the global Axios instance treats 4xx as
      // non-throwing (validateStatus < 500), so we must inspect each result.
      // Skip doctors that have already been contacted (pending/accepted).
      const userIds = doctorsToContact.map((d) => d.id);
      const results = await Promise.allSettled(
        userIds.map((userId) => {
          const doctor = doctors.find((d) => d.id === userId);
          const contactMethod: InterviewContactMethod[] = doctor?.phoneNumber
            ? ["email", "sms"]
            : ["email"];
          return api.interviews.addParticipant(interview.id, {
            userId,
            contactMethod,
            inviteSource: "search",
          });
        }),
      );

      // Partition into successes (2xx) and failures
      const failures: string[] = [];
      let successCount = 0;

      for (const result of results) {
        if (result.status === "rejected") {
          // Network error or unexpected throw
          const err = result.reason as { message?: string } | undefined;
          failures.push(err?.message ?? "Network error");
        } else {
          const status = result.value.status;
          if (status >= 200 && status < 300) {
            successCount++;
          } else {
            // 4xx response — extract error message from response body
            const data = result.value.data as
              { message?: string; reason?: string } | undefined;
            failures.push(
              data?.message ??
                data?.reason ??
                `Request failed (${String(status)})`,
            );
          }
        }
      }

      const firstFailure = failures[0];
      if (firstFailure !== undefined && successCount === 0) {
        // All failed — show the first error
        setSubmitError(firstFailure);
        setShowConfirmDialog(false);
      } else if (firstFailure !== undefined) {
        // Partial success
        renderSuccessToast({
          message: `Invited ${String(successCount)} of ${String(userIds.length)} doctor${userIds.length !== 1 ? "s" : ""}`,
        });
        setSubmitError(
          `${String(failures.length)} invitation${failures.length !== 1 ? "s" : ""} failed: ${firstFailure}`,
        );
        setShowConfirmDialog(false);
        reloadFormContext();
      } else {
        // All succeeded
        renderSuccessToast({
          message: `Invited ${String(selectedDoctorIds.size)} doctor${selectedDoctorIds.size !== 1 ? "s" : ""}`,
        });
        reloadFormContext();
        onClose();
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to add doctors to request. Please try again.";
      setSubmitError(message);
      setShowConfirmDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the selected doctors for the confirmation dialog
  const selectedDoctors = useMemo(
    () => doctors.filter((d) => selectedDoctorIds.has(d.id)),
    [doctors, selectedDoctorIds],
  );

  // Doctors that were selected but already contacted (pending/accepted)
  const alreadyContactedDoctors = useMemo(
    () =>
      selectedDoctors.filter((d) => {
        const status = existingStatusByUserId.get(d.id);
        return status === "pending" || status === "accepted";
      }),
    [selectedDoctors, existingStatusByUserId],
  );

  // Doctors to contact (excluding already contacted)
  const doctorsToContact = useMemo(() => {
    const contactedIds = new Set(alreadyContactedDoctors.map((d) => d.id));
    return selectedDoctors.filter((d) => !contactedIds.has(d.id));
  }, [selectedDoctors, alreadyContactedDoctors]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Count active filters for badge
  const activeFilterCount =
    selectedSpecialismIds.length +
    selectedGenders.length +
    selectedLanguageIds.length;

  // Add body overflow control when dialog is open on mobile.
  // We use useLayoutEffect (synchronous, before paint) so the lock is applied
  // in the same rendering phase as MUI's own scroll lock — preventing any
  // "scroll bleed" frame where the background can briefly scroll behind the
  // fullscreen overlay. This is safe because disableScrollLock={isMobile}
  // prevents MUI from running its competing useLayoutEffect on mobile, making
  // this effect the sole owner of the scroll lock on mobile.
  // We always restore to empty strings rather than captured values so that
  // the cleanup is correct even if another effect had already set the body
  // styles before this one ran.
  useLayoutEffect(() => {
    if (isMobile) {
      // Lock body scroll (iOS Safari requires position:fixed to prevent
      // the background page from scrolling behind a fullscreen overlay).
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.height = "100%";

      return () => {
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.width = "";
        document.body.style.height = "";
      };
    }
  }, [isMobile]);

  const requestedAvailabilityInput = (
    <AvailabilityFilterPopout
      value={requestedRanges}
      onChange={setRequestedRanges}
      now={now}
    />
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={true}
        onClose={onClose}
        maxWidth={isMobile ? false : "lg"}
        fullWidth={!isMobile}
        fullScreen={isMobile}
        data-testid="s12-doctor-request-dialog"
        disableScrollLock={isMobile}
        sx={{
          // Force dialog to be completely isolated from page
          "& .MuiDialog-container": {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
          },
          "& .MuiBackdrop-root": {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          },
        }}
        slotProps={{
          paper: {
            sx: {
              // On mobile: take full viewport using fixed positioning
              ...(isMobile && {
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                // Use dvh for mobile safari if supported, fallback to vh
                height: "100dvh",
                maxWidth: "100vw",
                maxHeight: "100dvh",
                margin: 0,
                borderRadius: 0,
              }),
              // On desktop: standard modal behavior
              ...(!isMobile && {
                height: "85vh",
                maxHeight: "85vh",
              }),
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          },
        }}
      >
        {/* Fixed Header */}
        <DialogTitle
          sx={{
            py: { xs: 1.5, md: 1.5 },
            px: { xs: 2, md: 2 },
            flexShrink: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
              }}
            >
              {isMobile ? "Request Doctor" : "Find Independent Doctor"}
            </Typography>
            <IconButton
              onClick={onClose}
              size={isMobile ? "medium" : "small"}
              edge="end"
              aria-label="close"
              data-testid="close-dialog-button"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Main Content */}
        <DialogContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            overflow: "hidden",
            p: 0,
            minWidth: 0,
            width: "100%",
            "&.MuiDialogContent-root": { p: 0 },
          }}
        >
          {/* Location Entry Pre-State */}
          {showLocationEntry ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: { xs: 3, md: 6 },
                textAlign: "center",
              }}
            >
              <LocationOnIcon
                sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                Where is the assessment taking place?
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 3,
                  maxWidth: 440,
                }}
              >
                Setting the assessment location helps calculate travel times for
                available doctors.
              </Typography>

              <Box sx={{ width: "100%", maxWidth: 400 }}>
                {!assessmentLocationUnknown && (
                  <AddressField
                    name="assessmentLocation"
                    label="Assessment Location"
                    addressLabel="Address"
                    postalLabel="Postcode"
                    googleMapsApiKey={config.googleMapsApiKey}
                    addressCheckBoxLabel=""
                    hideConfirmation
                    value={{
                      address: assessmentAddress,
                      postalCode: assessmentPostalCode,
                    }}
                    onChange={(values) => {
                      if (values) {
                        setAssessmentAddress(
                          (values as Record<string, string>).address ?? "",
                        );
                        setAssessmentPostalCode(
                          (values as Record<string, string>).postalCode ?? "",
                        );
                      }
                    }}
                    testId="assessment-location"
                  />
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={assessmentLocationUnknown}
                      onChange={(e) => {
                        setAssessmentLocationUnknown(e.target.checked);
                      }}
                      size="small"
                      data-testid="assessment-location-unknown-toggle"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Location not yet known
                    </Typography>
                  }
                  sx={{ mt: 1, alignSelf: "flex-start" }}
                />

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => void handleConfirmLocation()}
                  disabled={
                    isSavingLocation ||
                    (!assessmentLocationUnknown && !assessmentPostalCode.trim())
                  }
                  sx={{ mt: 3 }}
                  data-testid="confirm-location-button"
                >
                  {isSavingLocation ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Continue"
                  )}
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              {/* Mobile: Collapsible Filters at Top */}
              {isMobile ? (
                <Box
                  sx={{
                    flexShrink: 0,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    width: "100%",
                    overflow: "visible",
                  }}
                >
                  {/* Local Authority - Compact display on Mobile */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      bgcolor: "grey.50",
                    }}
                  >
                    {showMobileLAPicker || !selectedLocalAuthority ? (
                      <Box sx={{ flex: 1 }}>
                        <LocalAuthorityPicker
                          value={selectedLocalAuthority}
                          onChange={(la) => {
                            setSelectedLocalAuthority(la);
                            // Only collapse when a proper LA with id is selected (not just typed text)
                            if (la?.id) setShowMobileLAPicker(false);
                          }}
                          label="Local Authority"
                        />
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="caption"
                            component="div"
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            Local Authority
                          </Typography>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {selectedLocalAuthority.name}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          onClick={() => {
                            setShowMobileLAPicker(true);
                          }}
                          sx={{ ml: 1, flexShrink: 0 }}
                        >
                          Change
                        </Button>
                      </>
                    )}
                  </Box>

                  {/* Assessment Location - Compact read-only display on Mobile */}
                  <Box
                    sx={{
                      px: 2,
                      py: 0.75,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: "grey.50",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <LocationOnIcon
                      fontSize="small"
                      color="primary"
                      sx={{ flexShrink: 0 }}
                    />
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: "text.secondary",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {assessmentLocationUnknown
                        ? "Location unknown"
                        : assessmentPostalCode
                          ? `${assessmentAddress ? `${assessmentAddress}, ` : ""}${assessmentPostalCode}`
                          : "No location set"}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowLocationEntry(true)}
                      data-testid="edit-assessment-location-button"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Requested Availability - searched for alongside the location */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      bgcolor: "grey.50",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {requestedAvailabilityInput}
                  </Box>

                  {/* Filters Accordion - Collapsible */}
                  {selectedLocalAuthority?.id && (
                    <Accordion
                      disableGutters
                      elevation={0}
                      sx={{
                        "&:before": { display: "none" },
                        bgcolor: "grey.50",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          px: 2,
                          minHeight: 48,
                          "& .MuiAccordionSummary-content": { my: 1 },
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Badge
                            badgeContent={activeFilterCount}
                            color="primary"
                          >
                            <FilterListIcon fontSize="small" />
                          </Badge>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                            }}
                          >
                            Filters
                          </Typography>
                          {activeFilterCount > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                              }}
                            >
                              ({activeFilterCount} active)
                            </Typography>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                        <FilterPanel
                          languages={languages}
                          specialisms={specialisms}
                          selectedSpecialismIds={selectedSpecialismIds}
                          selectedGenders={selectedGenders}
                          selectedLanguageIds={selectedLanguageIds}
                          onSpecialismsChange={setSelectedSpecialismIds}
                          onGendersChange={setSelectedGenders}
                          onLanguageIdsChange={setSelectedLanguageIds}
                          onClearFilters={handleClearFilters}
                        />
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
              ) : (
                /* Desktop: Fixed Left Sidebar */
                <Box
                  sx={{
                    width: 340,
                    minWidth: 340,
                    maxWidth: 340,
                    borderRight: 1,
                    borderColor: "divider",
                    p: 2,
                    bgcolor: "background.default",
                    flexShrink: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {/* Assessment Location - Read-only chip */}
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <LocationOnIcon fontSize="small" color="primary" />
                        Assessment Location
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setShowLocationEntry(true)}
                        data-testid="edit-assessment-location-button"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {assessmentLocationUnknown
                        ? "Location not yet known"
                        : assessmentPostalCode
                          ? `${assessmentAddress ? `${assessmentAddress}, ` : ""}${assessmentPostalCode}`
                          : "No location set"}
                    </Typography>
                  </Box>
                  {/* Requested Availability - searched for alongside the location */}
                  <Box sx={{ mb: 2 }}>{requestedAvailabilityInput}</Box>
                  {/* Local Authority Picker */}
                  <Box sx={{ mb: 2 }}>
                    <LocalAuthorityPicker
                      value={selectedLocalAuthority}
                      onChange={setSelectedLocalAuthority}
                      label="Local Authority"
                    />
                  </Box>
                  {/* Filter Panel */}
                  {selectedLocalAuthority?.id && (
                    <FilterPanel
                      languages={languages}
                      specialisms={specialisms}
                      selectedSpecialismIds={selectedSpecialismIds}
                      selectedGenders={selectedGenders}
                      selectedLanguageIds={selectedLanguageIds}
                      onSpecialismsChange={setSelectedSpecialismIds}
                      onGendersChange={setSelectedGenders}
                      onLanguageIdsChange={setSelectedLanguageIds}
                      onClearFilters={handleClearFilters}
                    />
                  )}
                </Box>
              )}

              {/* Doctor List Area */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                  width: isMobile ? "100%" : undefined,
                  overflow: "hidden",
                }}
              >
                {/* Error Banners */}
                {(submitError || doctorsError) && (
                  <Box sx={{ p: 2, pb: 0, flexShrink: 0 }}>
                    {submitError && (
                      <Banner
                        bannerType={BannerList.ERROR}
                        sx={{ mb: 1 }}
                        title="Error submitting"
                        body={submitError}
                      />
                    )}
                    {doctorsError && (
                      <Banner
                        bannerType={BannerList.ERROR}
                        title="Failed to load doctors. Please refresh to try again."
                      />
                    )}
                  </Box>
                )}

                {/* No LA selected prompt */}
                {!selectedLocalAuthority?.id && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flex: 1,
                      color: "text.secondary",
                      p: 3,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body1">
                      Select a Local Authority to view available doctors
                    </Typography>
                  </Box>
                )}

                {/* Loading State */}
                {selectedLocalAuthority?.id && isLoadingDoctors && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}

                {/* Doctor List - SCROLLABLE */}
                {selectedLocalAuthority?.id &&
                  !isLoadingDoctors &&
                  !doctorsError && (
                    <Box
                      sx={{
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        p: { xs: 1.5, md: 2 },
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      {searchBlocked ? (
                        <EmptyState
                          message={`Every time you ask for must be at least ${String(ASSESSMENT_DURATION_MINUTES)} minutes long`}
                          suggestion={`Adjust ${unusable.map((range) => formatTimeRangeOnDate(range)).join(", ")} to search.`}
                          data-testid="s12-unusable-ranges-state"
                        />
                      ) : (
                        <DoctorList
                          doctors={doctors}
                          criteria={criteria}
                          selectedDoctorIds={selectedDoctorIds}
                          onToggleSelection={handleToggleDoctor}
                          existingStatusByUserId={existingStatusByUserId}
                          routeTimes={routeTimes}
                          routeTimesLoading={routeTimesLoading}
                          onContactDoctor={handleContactDoctor}
                          contactingDoctorIds={contactingDoctorIds}
                          now={now}
                        />
                      )}
                    </Box>
                  )}
              </Box>
            </>
          )}
        </DialogContent>

        {/* Fixed Footer - only show in doctor list view */}
        {!showLocationEntry && (
          <DialogActions
            sx={{
              px: { xs: 2, md: 2 },
              py: { xs: 1.5, md: 1.5 },
              borderTop: "1px solid",
              borderColor: "divider",
              flexDirection: "row",
              gap: 1,
              justifyContent: isMobile ? "center" : "space-between",
              alignItems: "center",
              flexShrink: 0,
              bgcolor: "background.paper",
            }}
          >
            {/* Left side — selection count (desktop) */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {!isMobile && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {selectedDoctorIds.size} doctor
                  {selectedDoctorIds.size !== 1 ? "s" : ""} selected
                </Typography>
              )}
            </Box>

            {/* Buttons */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => void handleMarkAsContacted()}
                disabled={selectedDoctorIds.size === 0 || isSubmitting}
                startIcon={<CheckCircleOutlineIcon />}
                data-testid="mark-as-contacted-button"
                size={isMobile ? "large" : "medium"}
              >
                Mark as Contacted
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={selectedDoctorIds.size === 0 || isSubmitting}
                startIcon={<SendIcon />}
                data-testid="add-doctors-button"
                fullWidth={isMobile}
                size={isMobile ? "large" : "medium"}
                sx={{ minHeight: isMobile ? 48 : "auto" }}
              >
                Contact {selectedDoctorIds.size} Doctor
                {selectedDoctorIds.size !== 1 ? "s" : ""}
              </Button>
            </Box>
          </DialogActions>
        )}
      </Dialog>
      {/* Confirmation Dialog */}
      <ContactDoctorsConfirmDialog
        open={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
        }}
        onConfirm={() => {
          void handleConfirmSubmit();
        }}
        isSubmitting={isSubmitting}
        doctors={doctorsToContact}
        alreadyContactedDoctors={alreadyContactedDoctors}
      />
    </LocalizationProvider>
  );
}
